import config from '../config';
import Logger from '../logger';
import event from '../mixins/event';
import ThemeBusinessMap from './ThemeBusinessMap';
import gsap from 'gsap';

/**
 * ThemeBusinessBrowser - Manages the business browser interface with filtering,
 * pagination, infinite scroll, and map integration.
 *
 * @class ThemeBusinessBrowser
 */
class ThemeBusinessBrowser {

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	/**
	 * Creates an instance of ThemeBusinessBrowser.
	 *
	 * @param {HTMLElement} dom - The root DOM element for the business browser.
	 * @throws {Error} Throws an error if dom is null or undefined.
	 */
	constructor(dom) {
		if (!dom) {
			throw new Error('ThemeBusinessBrowser: dom element is required');
		}

		this.NAME = 'ThemeBusinessBrowser';

		this.dom = dom;

		this.page = 1;
		this.limit = 9;
		this.offset = 0;

		this.defaultDisplayType = this.dom.getAttribute(`data-theme-business-browser-default-display-type`);
		this.defaultBusinessTag = this.dom.getAttribute(`data-theme-business-browser-default-business-tag`);
		this.defaultBusinessCategory = this.dom.getAttribute(`data-theme-business-browser-default-business-category`);
		this.defaultBusinessSubcategory = this.dom.getAttribute(`data-theme-business-browser-default-business-subcategory`);
		this.defaultBusinessType = this.dom.getAttribute(`data-theme-business-browser-business-type`);
		this.defaultSearch = this.dom.getAttribute(`data-theme-business-browser-default-search`);
		this.defaultShopLocal = this.dom.getAttribute(`data-theme-business-browser-default-shop-local`);

		this.businessTag = this.defaultBusinessTag;
		this.businessType = this.defaultBusinessType;
		this.businessCategory = this.defaultBusinessCategory;
		// Safely handle null/undefined defaultBusinessSubcategory
		this.businessSubcategory = (this.defaultBusinessSubcategory && this.defaultBusinessSubcategory.length > 0) ? this.defaultBusinessSubcategory : null;
		this.businessShopLocal = !!this.defaultShopLocal;

		this.displayType = this.defaultDisplayType || 'grid';

		this.browsePaneActiveClass = 'theme-business-browser__browse-pane--active';
		this.mapToggleSectionDom = this.dom.querySelector('.theme-business-browser__sidebar-map-toggle');
		this.browsePaneToggleDisplayDom = this.dom.querySelector(`[data-theme-business-browser-toggle-display]`);
		this.browsePaneToggleDisplayMapClass = 'theme-business-browser__sidebar-display-toggle--map';
		this.browsePaneToggleDisplayGridClass = 'theme-business-browser__sidebar-display-toggle--grid';

		this.businessTagsLinksDom = this.dom.querySelector(`[data-theme-business-browser-business-tags-links]`);
		this.businessTagsLinkDoms = this.dom.querySelectorAll(`[data-theme-business-browser-business-tag-link]`);

		this.filtersToggleDom = this.dom.querySelector(`[data-theme-business-browser-filters-toggle]`);
		this.filtersToggleActiveClass = 'theme-business-browser__main-tools--active';

		this.gridPaneDom = this.dom.querySelector(`[data-theme-business-browser-display-type="grid"]`);

		this.mainToolsDom = this.dom.querySelector(`[data-theme-business-browser-main-tools]`);

		this.searchFormDom = this.dom.querySelector(`[data-theme-business-browser-search-form]`);
		this.searchInputDom = this.dom.querySelector(`[data-theme-business-browser-search-input]`);

		this.shopLocalToggleDom = this.dom.querySelector(`[data-theme-business-browser-shop-local-toggle]`);
		this.shopLocalToggleActiveClass = 'theme-business-browser__sidebar-local-toggle--active';

		this.subcategoriesDom = this.dom.querySelector(`[data-theme-business-browser-subcategories]`);
		this.subcategoriesLinksDom = this.dom.querySelector(`[data-theme-business-browser-subcategories-links]`);
		this.subcategoriesLinkDoms = this.dom.querySelectorAll(`[data-theme-business-browser-subcategory-link]`);

		this.themeBusinessMap = null;
		this.themeBusinessMapDom = this.dom.querySelector(`[data-theme-business-map]`);
		this.themeBusinessMapPaneDom = this.dom.querySelector(`[data-theme-business-browser-display-type="map"]`);
		this.themeBusinessGridItemsDom = this.dom.querySelector(`[data-theme-business-browser-grid-items]`);

		this.themeBusinessCategoryLinkActiveClass = 'theme-business-browser__category-link--active';
		this.themeBusinessCategoryLinksDom = this.dom.querySelectorAll(`[data-theme-business-browser-category-link]`);

		this.businessTagLinkActiveClass = 'theme-business-browser__sidebar-link--active';
		this.businessTagLinkInactiveClass = 'theme-business-browser__sidebar-link--inactive';
		this.businessTagLinksDom = this.dom.querySelectorAll(`[data-theme-business-browser-business-tag-link]`);

		// Async / paging state
		this.isFetchingBusinesses = false;
		this.hasMoreBusinesses = true;

		// Infinite scroll references
		this.infiniteScrollObserver = null;
		this.infiniteScrollLoaderDom = null;

		// Load more: wrapper hides the whole row (margin + flex) so the UI actually disappears; button alone can leave a gap or lose to .theme-button display rules.
		this.gridItemsLoaderDom = this.dom.querySelector(`[data-theme-business-browser-grid-items-loader]`);
		this.loadMoreButtonDom = this.dom.querySelector(`[data-theme-business-browser-grid-items-loader-button]`);
	}

	/**
	 * Show or hide the load-more row. Inline display on the wrapper beats theme flex/button rules; `hidden` on wrapper + button matches semantics and shows in DevTools.
	 *
	 * @param {boolean} visible - True when more results may exist (full page returned).
	 * @returns {void}
	 */
	setLoadMoreUiVisible(visible) {
		const show = Boolean(visible);
		if (this.gridItemsLoaderDom) {
			this.gridItemsLoaderDom.style.display = show ? '' : 'none';
			this.gridItemsLoaderDom.toggleAttribute('hidden', !show);
		}
		if (this.loadMoreButtonDom) {
			this.loadMoreButtonDom.toggleAttribute('hidden', !show);
		}
	}

	/**
	 * Initialize the component and set up event listeners.
	 * Must be called after instantiation to activate the browser.
	 *
	 * @returns {void}
	 */
	init() {
		Logger.info(`${this.NAME} > init`);
		this.initThemeBusinessMap();

		// Listen for ThemeBusinessMap:map_loaded event on this.themeBusinessMap
		if (this.themeBusinessMap) {
			this.themeBusinessMap.on(`${this.themeBusinessMap.NAME}:map_loaded`, () => {
				this.initBrowsePaneToggleDisplay();
				this.initFiltersToggle();
				this.initBusinessTagLinks();
				this.fetchBusinessTags();
				this.initLoadMoreButton();
				// this.initInfiniteScroll();
				this.initSearchForm();
				// this.initShopLocalToggle();
				this.initThemeBusinessCategoryLinks();
				this.initSubcategoryLinks();

				this.trigger(`${this.NAME}:loaded`);
			});
		}

		this.on(`${this.NAME}:filters_changed`, () => {
			this.applyFiltersAndState();
		});

		this.on(`${this.NAME}:loaded`, () => {
			this.applyFiltersAndState();
		});

	}

	/**
	 * Central handler for when filters or initial load change.
	 * Resets pagination, fetches businesses, updates UI state, and syncs URL.
	 *
	 * @returns {void}
	 */
	applyFiltersAndState() {
		Logger.info(`${this.NAME} > applyFiltersAndState`);
		this.resetPaging();
		this.fetchBusinesses();
		this.setPresentationState();
		this.setUrlState();
	}

	/**
	 * Fetch businesses from the server based on current filters and pagination.
	 *
	 * @param {boolean} [append=false] - If true, append results to existing list; otherwise replace.
	 * @returns {Promise<Object|null>} Returns data object with businesses_grid_items and map_data, or null on error.
	 */
	async fetchBusinesses(append = false) {
		Logger.info(`${this.NAME} > fetchBusinesses`);

		// Prevent overlapping requests
		if (this.isFetchingBusinesses) {
			Logger.info(`${this.NAME} > fetchBusinesses skipped (already fetching)`);
			return null;
		}

		// Validate required globals
		if (!window.global_vars || !window.global_vars.ajax_url || !window.global_vars.ajax_nonce) {
			Logger.error(`${this.NAME} > fetchBusinesses: Missing required global variables`);
			return null;
		}

		this.isFetchingBusinesses = true;

		// When starting a fresh fetch (e.g. filters changed), reset "has more"
		if (!append) {
			this.hasMoreBusinesses = true;

			this.setLoadMoreUiVisible(true);

			// Re-attach the observer if we previously detached it
			if (this.infiniteScrollObserver && this.infiniteScrollLoaderDom) {
				this.infiniteScrollObserver.observe(this.infiniteScrollLoaderDom);
			}
		}

		// If there is a load more button, capture the original text
		let loadMoreButtonOriginalText = '';
		if (this.loadMoreButtonDom) {
			loadMoreButtonOriginalText = this.loadMoreButtonDom.textContent;
		}

		// If there is a load more button, set the text to loading
		if (this.loadMoreButtonDom) {
			this.loadMoreButtonDom.textContent = 'Loading...';
			this.loadMoreButtonDom.disabled = true;
		}

		let data = {
			businesses_grid_items: [],
			map_data: [],
		};

		try {
			const formData = new FormData();

			formData.append('action', 'wonder_get_business_browse_items');
			formData.append('nonce', window.global_vars.ajax_nonce);
			formData.append('page', String(this.page));
			formData.append('limit', String(this.limit));
			formData.append('offset', String(this.offset));
			formData.append('business_tag', this.businessTag === 'all' ? '' : (this.businessTag || ''));
			formData.append('business_type', this.businessType || '');
			// formData.append('business_shop_local', this.businessShopLocal ? 'true' : 'false');

			// If subcategory, use the subcategory - otherwise use the category
			if (this.businessSubcategory) {
				formData.append('business_category', this.businessSubcategory);
			} else {
				formData.append('business_category', this.businessCategory || '');
			}

			// Add the search input value
			const searchInputDom = this.dom.querySelector(`[data-theme-business-browser-search-input]`);
			if (searchInputDom && searchInputDom.value && searchInputDom.value.trim().length > 0) {
				formData.append('search', searchInputDom.value.trim());
			}

			const url = window.global_vars.ajax_url;

			// Fetch from wp-admin
			const response = await fetch(url, {
				method: 'POST',
				body: formData,
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			// Parse JSON with error handling
			try {
				data = await response.json();
			} catch (jsonError) {
				Logger.error(`${this.NAME} > fetchBusinesses: Invalid JSON response`, jsonError);
				throw new Error('Invalid JSON response from server');
			}

			// Defensive: normalize expected fields
			if (!Array.isArray(data.businesses_grid_items)) {
				data.businesses_grid_items = [];
			}

			if (!Array.isArray(data.map_data)) {
				data.map_data = [];
			}

			if (this.themeBusinessMap) {
				this.themeBusinessMap.setNewMarkerData(data.map_data);
			}

			this.renderBusinesses(data.businesses_grid_items, append);

			// If we received fewer than the limit, assume there are no more results
			if (data.businesses_grid_items.length < this.limit) {
				this.hasMoreBusinesses = false;
				if (this.infiniteScrollObserver && this.infiniteScrollLoaderDom) {
					this.infiniteScrollObserver.unobserve(this.infiniteScrollLoaderDom);
				}
			}

			this.setLoadMoreUiVisible(data.businesses_grid_items.length >= this.limit);

			this.page++;
			this.offset += this.limit;

			return data;
		} catch (error) {
			Logger.error(`${this.NAME} > fetchBusinesses error: ${error}`);

			// Stop infinite scroll on hard failure
			this.hasMoreBusinesses = false;
			if (this.infiniteScrollObserver && this.infiniteScrollLoaderDom) {
				this.infiniteScrollObserver.unobserve(this.infiniteScrollLoaderDom);
			}

			return null;
		} finally {
			this.isFetchingBusinesses = false;

			if (this.loadMoreButtonDom) {
				this.loadMoreButtonDom.disabled = false;
				this.loadMoreButtonDom.textContent = loadMoreButtonOriginalText;
			}
		}
	}

	/**
	 * Fetch subcategories for the current business category.
	 *
	 * @returns {Promise<Object>} Returns data object with subcategories array, or empty object on error.
	 */
	async fetchSubcategories() {
		Logger.info(`${this.NAME} > fetchSubcategories`);

		// If no business category, return
		if (!this.businessCategory) {
			return { subcategories: [] };
		}

		// Validate required globals
		if (!window.global_vars || !window.global_vars.ajax_url || !window.global_vars.ajax_nonce) {
			Logger.error(`${this.NAME} > fetchSubcategories: Missing required global variables`);
			return { subcategories: [] };
		}

		// Reset the subcategories safely
		if (this.subcategoriesLinksDom) {
			this.subcategoriesLinksDom.innerHTML = '';
		}

		// Fetch the subcategories with proper URL encoding
		try {
			const businessCategory = encodeURIComponent(this.businessCategory);
			const url = `${window.global_vars.ajax_url}?action=wonder_get_business_subcategories&nonce=${encodeURIComponent(window.global_vars.ajax_nonce)}&business_category=${businessCategory}`;

			const response = await fetch(url);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			let data;
			try {
				data = await response.json();
			} catch (jsonError) {
				Logger.error(`${this.NAME} > fetchSubcategories: Invalid JSON response`, jsonError);
				return { subcategories: [] };
			}

			// Validate response structure
			if (!data || !Array.isArray(data.subcategories)) {
				Logger.warn(`${this.NAME} > fetchSubcategories: Invalid response structure`);
				data.subcategories = [];
			}

			this.renderSubcategories(data.subcategories);
			return data;
		} catch (error) {
			Logger.error(`${this.NAME} > fetchSubcategories error: ${error}`);
			return { subcategories: [] };
		}
	}

	/**
	 * Fetch business tags for the current category/subcategory.
	 *
	 * @returns {Promise<Object>} Returns data object with tags array, or empty object on error.
	 */
	async fetchBusinessTags() {
		Logger.info(`${this.NAME} > fetchBusinessTags`);

		// Validate required globals
		if (!window.global_vars || !window.global_vars.ajax_url || !window.global_vars.ajax_nonce) {
			Logger.error(`${this.NAME} > fetchBusinessTags: Missing required global variables`);
			return { tags: [] };
		}

		// Fetch the business tags
		try {
			const businessType = this.businessType || null;
			const businessCategory = this.businessSubcategory || this.businessCategory || null;

			// Build base URL with proper encoding; include business_type and business_category when we actually have one
			let url = `${window.global_vars.ajax_url}?action=wonder_get_business_tags&nonce=${encodeURIComponent(window.global_vars.ajax_nonce)}`;

			if (businessCategory !== null) {
				url += `&business_category=${encodeURIComponent(businessCategory)}`;
			} else if (businessType !== null) {
				url += `&business_type=${encodeURIComponent(businessType)}`;
			}

			const response = await fetch(url);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			let data;
			try {
				data = await response.json();
			} catch (jsonError) {
				Logger.error(`${this.NAME} > fetchBusinessTags: Invalid JSON response`, jsonError);
				return { tags: [] };
			}

			// Validate response structure
			if (!data || !Array.isArray(data.tags)) {
				Logger.warn(`${this.NAME} > fetchBusinessTags: Invalid response structure`);
				data.tags = [];
			}

			this.renderBusinessTags(data.tags);
			return data;
		} catch (error) {
			Logger.error(`${this.NAME} > fetchBusinessTags error: ${error}`);
			return { tags: [] };
		}
	}

	/**
	 * Initialize the browse pane display toggle (grid/map switcher).
	 *
	 * @returns {void}
	 */
	initBrowsePaneToggleDisplay() {
		Logger.info(`${this.NAME} > initBrowsePaneToggleDisplay`);

		if (!this.browsePaneToggleDisplayDom) return;
		this.browsePaneToggleDisplayDom.addEventListener(`click`, (e) => {
			e.preventDefault();
			this.toggleDisplay();
		});
	}

	/**
	 * Initialize the filters panel toggle button.
	 *
	 * @returns {void}
	 */
	initFiltersToggle() {
		Logger.info(`${this.NAME} > initFiltersToggle`);

		if (!this.filtersToggleDom) return;
		this.filtersToggleDom.addEventListener(`click`, (e) => {
			e.preventDefault();
			this.toggleFilters();
		});
	}

	/**
	 * Initialize infinite scroll using IntersectionObserver.
	 * Automatically loads more businesses when user scrolls near bottom.
	 *
	 * @returns {void}
	 */
	initInfiniteScroll() {
		Logger.info(`${this.NAME} > initInfiniteScroll`);

		if (!this.gridPaneDom) return;

		// Loader element that triggers infinite scroll
		const loaderDom = this.gridPaneDom.querySelector('[data-theme-business-browser-grid-items-loader]');
		if (!loaderDom) return;

		this.infiniteScrollLoaderDom = loaderDom;

		// When the bottom of the grid pane is reached, fetch the businesses
		const observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;

				// Respect loading and "no more results" state
				if (this.isFetchingBusinesses || !this.hasMoreBusinesses) {
					return;
				}

				this.fetchBusinesses(true); // true = append
			});
		}, {
			rootMargin: '0px 0px 200px 0px',
			threshold: 0
		});

		this.infiniteScrollObserver = observer;
		observer.observe(loaderDom);
	}

	/**
	 * Initialize the load more button event handler.
	 *
	 * @returns {void}
	 */
	initLoadMoreButton() {
		Logger.info(`${this.NAME} > initLoadMoreButton`);

		if (!this.loadMoreButtonDom) return;

		this.loadMoreButtonDom.addEventListener(`click`, (e) => {
			e.preventDefault();
			this.fetchBusinesses(true);
		});
	}

	/**
	 * Initialize search form event handlers (submit and blur).
	 *
	 * @returns {void}
	 */
	initSearchForm() {
		Logger.info(`${this.NAME} > initSearchForm`);

		if (!this.searchFormDom) return;

		// On submit, fetch the businesses
		this.searchFormDom.addEventListener(`submit`, (e) => {
			e.preventDefault();
			this.trigger(`${this.NAME}:filters_changed`);
		});

		// On blur, fetch the businesses
		if (this.searchInputDom) {
			this.searchInputDom.addEventListener(`blur`, (e) => {
				e.preventDefault();
				this.trigger(`${this.NAME}:filters_changed`);
			});
		}
	}

	/**
	 * Initialize the "Shop Local" filter toggle.
	 *
	 * @returns {void}
	 */
	initShopLocalToggle() {
		Logger.info(`${this.NAME} > initShopLocalToggle`);

		if (!this.shopLocalToggleDom) return;
		this.shopLocalToggleDom.addEventListener(`click`, (e) => {
			e.preventDefault();
			this.toggleShopLocal();
		});
	}

	/**
	 * Initialize click handlers for business category filter links.
	 *
	 * @returns {void}
	 */
	initThemeBusinessCategoryLinks() {
		Logger.info(`${this.NAME} > initThemeBusinessCategoryLinks`);
		this.themeBusinessCategoryLinksDom.forEach((categoryLink) => {
			categoryLink.addEventListener(`click`, (e) => {
				e.preventDefault();
				const category = categoryLink.getAttribute(`data-theme-business-browser-category-link`);
				if (category === 'all' || category === this.businessCategory) {
					this.businessCategory = null;
				} else {
					this.businessCategory = category;
				}

				this.trigger(`${this.NAME}:category_changed`, this.businessCategory);
				this.trigger(`${this.NAME}:filters_changed`);
			});
		});
	}

	/**
	 * Initialize subcategory links and fetch subcategories when category changes.
	 *
	 * @returns {void}
	 */
	initSubcategoryLinks() {
		Logger.info(`${this.NAME} > initSubcategoryLinks`);

		this.fetchSubcategories();

		this.on(`${this.NAME}:category_changed`, (category) => {
			this.businessSubcategory = null;
			this.fetchSubcategories();
		});
	}

	/**
	 * Initialize business tag links and fetch tags when category changes.
	 *
	 * @returns {void}
	 */
	initBusinessTagLinks() {
		Logger.info(`${this.NAME} > initBusinessTagLinks`);
		this.on(`${this.NAME}:category_changed`, (category) => {
			this.businessTag = null;
			this.fetchBusinessTags(category);
		});
	}

	/**
	 * Initialize the ThemeBusinessMap component if map container exists.
	 *
	 * @returns {void}
	 */
	initThemeBusinessMap() {
		Logger.info(`${this.NAME} > initThemeBusinessMap`);

		if (this.themeBusinessMap) return;

		if (!this.themeBusinessMapDom) return;

		this.themeBusinessMap = new ThemeBusinessMap(this.themeBusinessMapDom);
		this.themeBusinessMap.init();
	}



	/**
	 * Render businesses HTML into the grid container.
	 * Note: Assumes business HTML is already sanitized server-side.
	 *
	 * @param {Array<string>} businesses - Array of HTML strings for business items.
	 * @param {boolean} [append=false] - If true, append to existing content; otherwise replace.
	 * @returns {void}
	 */
	renderBusinesses(businesses, append) {
		Logger.info(`${this.NAME} > renderBusinesses`);

		if (!this.themeBusinessGridItemsDom) {
			Logger.warn(`${this.NAME} > renderBusinesses: Grid items container not found`);
			return;
		}

		// Validate input
		if (!Array.isArray(businesses)) {
			Logger.warn(`${this.NAME} > renderBusinesses: Invalid businesses array`);
			return;
		}

		// Track existing item count before rendering (for append mode)
		const existingItemCount = append
			? this.themeBusinessGridItemsDom.querySelectorAll('.theme-business-browser__grid-item').length
			: 0;

		// Loop through all businesses and wrap in <div class="theme-business-browser__grid-item">
		// Note: Business HTML should be sanitized server-side before being passed here
		const businessesHtml = businesses.map((business) => {
			if (typeof business !== 'string') {
				Logger.warn(`${this.NAME} > renderBusinesses: Invalid business item type`);
				return '';
			}
			return `<div class="theme-business-browser__grid-item">${business}</div>`;
		}).join('');

		// Render the businesses
		if (append) {
			// Use insertAdjacentHTML to avoid rewriting existing DOM (prevents flash)
			this.themeBusinessGridItemsDom.insertAdjacentHTML('beforeend', businessesHtml);
		} else {
			this.themeBusinessGridItemsDom.innerHTML = businessesHtml;
		}

		// Animate the newly added business items with GSAP
		this.animateBusinessItems(append, existingItemCount);
	}

	/**
	 * Animate business grid items using GSAP for graceful loading.
	 *
	 * @param {boolean} [append=false] - If true, only animate newly appended items; otherwise animate all items.
	 * @param {number} [previousItemCount=0] - Count of items before appending (used to identify new items).
	 * @returns {void}
	 */
	animateBusinessItems(append = false, previousItemCount = 0) {
		if (!this.themeBusinessGridItemsDom) return;

		// Get all grid items
		const allItems = this.themeBusinessGridItemsDom.querySelectorAll('.theme-business-browser__grid-item');

		if (!allItems || allItems.length === 0) return;

		// Determine which items to animate
		let itemsToAnimate;
		if (append && previousItemCount > 0) {
			// When appending, only animate the newly added items (items after the previous count)
			const newItems = Array.from(allItems).slice(previousItemCount);
			itemsToAnimate = newItems;
		} else {
			// For initial load or when previous count is unknown, animate all items
			itemsToAnimate = Array.from(allItems);
		}

		if (itemsToAnimate.length === 0) return;

		// Set initial state for items to animate
		gsap.set(itemsToAnimate, {
			opacity: 0,
			y: 30,
			scale: 0.95
		});

		// Animate items with stagger for a cascading effect
		gsap.to(itemsToAnimate, {
			opacity: 1,
			y: 0,
			scale: 1,
			duration: 0.6,
			ease: 'power2.out',
			stagger: {
				amount: 0.4, // Total stagger duration
				from: 'start'
			}
		});
	}

	/**
	 * Render business tags into the sidebar with proper XSS protection.
	 *
	 * @param {Array<Object>} tags - Array of tag objects with slug and name properties.
	 * @returns {void}
	 */
	renderBusinessTags(tags) {
		Logger.info(`${this.NAME} > renderBusinessTags`);

		if (!this.businessTagsLinksDom) {
			Logger.warn(`${this.NAME} > renderBusinessTags: Tags container not found`);
			return;
		}

		// Validate input
		if (!Array.isArray(tags)) {
			Logger.warn(`${this.NAME} > renderBusinessTags: Invalid tags array`);
			tags = [];
		}

		/**
		 * Escape HTML to prevent XSS attacks.
		 *
		 * @param {string} str - String to escape.
		 * @returns {string} Escaped string.
		 */
		const escapeHtml = (str) => {
			if (typeof str !== 'string') return '';
			const div = document.createElement('div');
			div.textContent = str;
			return div.innerHTML;
		};

		this.businessTagsLinksDom.innerHTML = '';

		// Real <button> elements, not <a href="#">: this is a pure in-page filter
		// toggle with no navigable destination of its own (business-tag terms have
		// no dedicated archive page), so the "link" role was misleading — screen
		// readers announce <a> as something that navigates somewhere. type="button"
		// also gets native Space-key activation, which a bare <a> lacks.

		// Add an "All" button
		this.businessTagsLinksDom.innerHTML += `<div class="theme-business-browser__sidebar-tag"><button type="button" class="theme-business-browser__sidebar-link" data-theme-business-browser-business-tag-link="all">All</button></div>`;

		tags.forEach((tag) => {
			if (!tag || typeof tag !== 'object') {
				Logger.warn(`${this.NAME} > renderBusinessTags: Invalid tag object`);
				return;
			}

			const slug = escapeHtml(tag.slug || '');
			const name = escapeHtml(tag.name || '');

			if (!slug || !name) {
				Logger.warn(`${this.NAME} > renderBusinessTags: Tag missing slug or name`);
				return;
			}

			this.businessTagsLinksDom.innerHTML += `<div class="theme-business-browser__sidebar-tag"><button type="button" class="theme-business-browser__sidebar-link" data-theme-business-browser-business-tag-link="${slug}">${name}</button></div>`;
		});

		this.businessTagsLinkDoms = this.dom.querySelectorAll(`[data-theme-business-browser-business-tag-link]`);
		this.businessTagLinksDom = this.businessTagsLinkDoms;

		this.businessTagsLinkDoms.forEach((businessTagsButton) => {
			businessTagsButton.addEventListener(`click`, () => {
				this.businessTag = businessTagsButton.getAttribute(`data-theme-business-browser-business-tag-link`);
				this.trigger(`${this.NAME}:filters_changed`);
			});
		});

		// If no specific tag is selected, default internal state to "all"
		if (!this.businessTag) {
			this.businessTag = 'all';
		}

		// Ensure tag highlighting reflects current state immediately after render
		this.setPresentationState();
	}

	/**
	 * Render subcategories into the sidebar with proper XSS protection.
	 *
	 * @param {Array<Object>} subcategories - Array of subcategory objects with slug and name properties.
	 * @returns {void}
	 */
	renderSubcategories(subcategories) {
		Logger.info(`${this.NAME} > renderSubcategories`);

		if (!this.subcategoriesLinksDom) {
			Logger.warn(`${this.NAME} > renderSubcategories: Subcategories container not found`);
			return;
		}

		// Validate input
		if (!Array.isArray(subcategories)) {
			Logger.warn(`${this.NAME} > renderSubcategories: Invalid subcategories array`);
			subcategories = [];
		}

		/**
		 * Escape HTML to prevent XSS attacks.
		 *
		 * @param {string} str - String to escape.
		 * @returns {string} Escaped string.
		 */
		const escapeHtml = (str) => {
			if (typeof str !== 'string') return '';
			const div = document.createElement('div');
			div.textContent = str;
			return div.innerHTML;
		};

		this.subcategoriesLinksDom.innerHTML = '';

		// "All" clears the subcategory filter, i.e. the parent category itself —
		// link there directly when we know it, same reasoning as the per-subcategory
		// hrefs below. Falls back to "#" only if the parent slug isn't available.
		const allSubcategoriesHref = this.businessCategory ? `/businesses/${this.businessCategory}/` : '#';
		this.subcategoriesLinksDom.innerHTML += `<div class="theme-business-browser__sidebar-tag"><a href="${allSubcategoriesHref}" class="theme-business-browser__sidebar-link" data-theme-business-browser-subcategory-link="all">All</a></div>`;

		subcategories.forEach((subcategory) => {
			if (!subcategory || typeof subcategory !== 'object') {
				Logger.warn(`${this.NAME} > renderSubcategories: Invalid subcategory object`);
				return;
			}

			const slug = escapeHtml(subcategory.slug || '');
			const name = escapeHtml(subcategory.name || '');

			if (!slug || !name) {
				Logger.warn(`${this.NAME} > renderSubcategories: Subcategory missing slug or name`);
				return;
			}

			// href points at the real flat business-category URL (/businesses/{slug}/),
			// not a bare slug: a bare slug resolves *relative to the current page path*
			// (e.g. /businesses/fashion-and-retail/bookstores-fashion-and-retail on
			// /businesses/fashion-and-retail/), which 404s — the taxonomy itself is flat,
			// so subcategories never actually nest under their parent's URL. Click still
			// filters in-page via preventDefault() below; this only matters for a real
			// destination on new-tab/middle-click and for crawlers reading the rendered DOM.
			this.subcategoriesLinksDom.innerHTML += `<div class="theme-business-browser__sidebar-tag"><a href="/businesses/${slug}/" class="theme-business-browser__sidebar-link" data-theme-business-browser-subcategory-link="${slug}">${name}</a></div>`;
		});

		this.subcategoriesLinkDoms = this.dom.querySelectorAll(`[data-theme-business-browser-subcategory-link]`);

		// Listen for click events on the subcategory links
		this.subcategoriesLinkDoms.forEach((subcategoryLink) => {
			subcategoryLink.addEventListener(`click`, (e) => {
				e.preventDefault();
				const subcategoryValue = subcategoryLink.getAttribute(`data-theme-business-browser-subcategory-link`);
				// If "All" is clicked, set to null so it falls back to main category
				this.businessSubcategory = subcategoryValue === 'all' ? null : subcategoryValue;
				this.trigger(`${this.NAME}:subcategory_changed`, this.businessSubcategory);
				this.trigger(`${this.NAME}:filters_changed`);
			});
		});

		this.setPresentationState();
	}



	/**
	 * Reset pagination state to initial values.
	 *
	 * @returns {void}
	 */
	resetPaging() {
		Logger.info(`${this.NAME} > resetPaging`);
		this.page = 1;
		this.offset = 0;
		this.hasMoreBusinesses = true;
	}

	/**
	 * Update UI presentation state to reflect current filter selections.
	 * Updates active classes on category, subcategory, and tag links.
	 *
	 * @returns {void}
	 */
	setPresentationState() {
		Logger.info(`${this.NAME} > setPresentationState`);

		// Set the active class to the clicked category link
		this.themeBusinessCategoryLinksDom.forEach((categoryLink) => {

			// If the category link is the same as the state, add the active class
			if (categoryLink.getAttribute(`data-theme-business-browser-category-link`) === this.businessCategory) {
				categoryLink.classList.add(this.themeBusinessCategoryLinkActiveClass);
			} else {
				categoryLink.classList.remove(this.themeBusinessCategoryLinkActiveClass);
			}
		});

		// Set the active class to the clicked subcategory link
		this.subcategoriesLinkDoms.forEach((subcategoryLink) => {
			if (subcategoryLink.getAttribute(`data-theme-business-browser-subcategory-link`) === this.businessSubcategory || (this.businessSubcategory === null && subcategoryLink.getAttribute(`data-theme-business-browser-subcategory-link`) === 'all')) {
				subcategoryLink.classList.add(this.businessTagLinkActiveClass);
				subcategoryLink.classList.remove(this.businessTagLinkInactiveClass);
			} else {
				subcategoryLink.classList.remove(this.businessTagLinkActiveClass);
				subcategoryLink.classList.add(this.businessTagLinkInactiveClass);
			}
		});

		// Set the active class to the clicked business tag link
		this.businessTagLinksDom.forEach((businessTagLink) => {
			if (businessTagLink.getAttribute(`data-theme-business-browser-business-tag-link`) === this.businessTag || (this.businessTag === null && businessTagLink.getAttribute(`data-theme-business-browser-business-tag-link`) === 'all')) {
				businessTagLink.classList.add(this.businessTagLinkActiveClass);
				businessTagLink.classList.remove(this.businessTagLinkInactiveClass);
			} else {
				businessTagLink.classList.remove(this.businessTagLinkActiveClass);
				businessTagLink.classList.add(this.businessTagLinkInactiveClass);
			}
		});

		// Show/hide the entire subcategories block based on whether a category or subcategory is selected
		if (this.subcategoriesDom) {
			const hasCategorySelection = !!(this.businessCategory || this.businessSubcategory);
			this.subcategoriesDom.style.display = hasCategorySelection ? '' : 'none';
		}
	}



	/**
	 * Update browser URL to reflect current filter state without page reload.
	 *
	 * @returns {string} The updated URL string.
	 */
	setUrlState() {
		Logger.info(`${this.NAME} > setUrlState`);

		let url;
		try {
			url = new URL(window.location.href);
		} catch (error) {
			Logger.error(`${this.NAME} > setUrlState: Invalid URL`, error);
			return '';
		}

		if (this.businessTag && this.businessTag !== 'all') {
			url.searchParams.set('business-tag', this.businessTag);
		} else {
			url.searchParams.delete('business-tag');
		}

		// If subcategory, use the subcategory - otherwise use the category
		if (this.businessSubcategory) {
			url.searchParams.set('business-category', this.businessSubcategory);
		} else if (this.businessCategory) {
			url.searchParams.set('business-category', this.businessCategory);
		} else {
			url.searchParams.delete('business-category');
		}

		if (this.displayType) {
			url.searchParams.set('display-type', this.displayType);
		} else {
			url.searchParams.set('display-type', 'grid');
		}

		// if (this.businessShopLocal) {
		// 	url.searchParams.set('shop-local', this.businessShopLocal ? 'true' : 'false');
		// } else {
		// 	url.searchParams.set('shop-local', 'false');
		// }

		window.history.pushState({}, '', url.toString());
		return url.toString();
	}

	/**
	 * Show the grid display pane and hide the map pane.
	 *
	 * @returns {void}
	 */
	showGridPane() {
		Logger.info(`${this.NAME} > showGridPane`);
		this.displayType = 'grid';

		if (this.gridPaneDom) {
			this.gridPaneDom.classList.add(this.browsePaneActiveClass);
		}

		if (this.themeBusinessMapPaneDom) {
			this.themeBusinessMapPaneDom.classList.remove(this.browsePaneActiveClass);
		}

		if (this.browsePaneToggleDisplayDom && this.browsePaneToggleDisplayDom.parentNode) {
			this.browsePaneToggleDisplayDom.parentNode.classList.remove(this.browsePaneToggleDisplayMapClass);
			this.browsePaneToggleDisplayDom.parentNode.classList.add(this.browsePaneToggleDisplayGridClass);
		}
	}

	/**
	 * Show the map display pane and hide the grid pane.
	 *
	 * @returns {void}
	 */
	showMapPane() {
		Logger.info(`${this.NAME} > showMapPane`);
		this.displayType = 'map';

		if (this.themeBusinessMapPaneDom) {
			this.themeBusinessMapPaneDom.classList.add(this.browsePaneActiveClass);
		}

		if (this.gridPaneDom) {
			this.gridPaneDom.classList.remove(this.browsePaneActiveClass);
		}

		if (this.browsePaneToggleDisplayDom && this.browsePaneToggleDisplayDom.parentNode) {
			this.browsePaneToggleDisplayDom.parentNode.classList.remove(this.browsePaneToggleDisplayGridClass);
			this.browsePaneToggleDisplayDom.parentNode.classList.add(this.browsePaneToggleDisplayMapClass);
		}
	}

	/**
	 * Toggle between grid and map display modes.
	 *
	 * @returns {void}
	 */
	toggleDisplay() {
		Logger.info(`${this.NAME} > toggleDisplay`);

		// Check current display type or map pane state to determine which view is active
		const isMapActive = this.displayType === 'map' ||
			(this.themeBusinessMapPaneDom && this.themeBusinessMapPaneDom.classList.contains(this.browsePaneActiveClass));

		if (isMapActive) {
			this.showGridPane();
		} else {
			this.showMapPane();
		}

		// Set the url state
		this.setUrlState();
	}

	/**
	 * Toggle the visibility of the filters panel.
	 *
	 * @returns {void}
	 */
	toggleFilters() {
		Logger.info(`${this.NAME} > toggleFilters`);

		if (!this.mainToolsDom) {
			Logger.warn(`${this.NAME} > toggleFilters: Main tools container not found`);
			return;
		}

		this.mainToolsDom.classList.toggle(this.filtersToggleActiveClass);
	}

	/**
	 * Toggle the "Shop Local" filter state.
	 *
	 * @returns {void}
	 */
	toggleShopLocal() {
		Logger.info(`${this.NAME} > toggleShopLocal`);

		if (!this.shopLocalToggleDom || !this.shopLocalToggleDom.parentNode) {
			this.businessShopLocal = !this.businessShopLocal;
			this.trigger(`${this.NAME}:filters_changed`);
			return;
		}

		this.businessShopLocal = !this.businessShopLocal;
		if (this.businessShopLocal) {
			this.shopLocalToggleDom.parentNode.classList.add(this.shopLocalToggleActiveClass);
		} else {
			this.shopLocalToggleDom.parentNode.classList.remove(this.shopLocalToggleActiveClass);
		}
		this.trigger(`${this.NAME}:filters_changed`);
	}
}

Object.assign(ThemeBusinessBrowser.prototype, event);

export default ThemeBusinessBrowser;
